/**
 * What the API said when it refused something, in the words to show.
 *
 * A rule a service enforces comes back as the error's own message. A rule the
 * request schema enforces comes back as "Some of the values sent are not
 * valid." with the real sentence in `details` — against a field where one
 * field is at fault, and against none where the rule is between fields (a
 * price under its minimum). Both are the server's decision; a screen shows
 * them, it does not re-derive them.
 */

import { ApiError } from '@connect/web-shared';

export interface Refusal {
  /** By field name, for the input it belongs under. */
  fields: Record<string, string>;
  /** Everything that names no field. */
  general: string[];
}

// Pydantic prefixes a validator's own sentence with this; the sentence is
// what was written for a person.
const clean = (message: string) => message.replace(/^Value error, /, '');

export function refusal(error: unknown): Refusal | null {
  if (!(error instanceof ApiError)) return null;
  if (error.details.length === 0) return { fields: {}, general: [error.message] };
  const fields: Record<string, string> = {};
  const general: string[] = [];
  for (const detail of error.details) {
    if (detail.field) fields[detail.field] = clean(detail.message);
    else general.push(clean(detail.message));
  }
  return { fields, general };
}

/** The whole refusal as one line, for a toast or a line under a dialog. */
export function refusalText(error: unknown): string | undefined {
  const found = refusal(error);
  if (!found) return undefined;
  return [...found.general, ...Object.values(found.fields)].join(' ') || undefined;
}
